// src/App.jsx
import React, { useState, useCallback, useMemo, useEffect, useRef, Suspense } from "react";
// Ensure Firebase is initialized before any auth usage
import './config/firebase-config.js';
import { AuthProvider, AuthModal, useAuth } from './components/auth/auth-components';
import { GameProvider } from './context/GameContext';
import { QueryProvider } from './providers/QueryProvider';
import SportsDataService from './services/sportsdata-service.js';
import GameInterface from './components/layout/GameInterface.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import FirebaseErrorBoundary from './components/FirebaseErrorBoundary.jsx';
import GroupSelection from './components/layout/GroupSelection.jsx';
import GroupCreationFlow from './components/forms/GroupCreationFlow.jsx';
import JoinGroupFlow from './components/layout/JoinGroupFlow.jsx';
import TeamSelection from './components/forms/TeamSelection.jsx';
import ProfileSetup from './components/forms/ProfileSetup.jsx';
import GameSelector from './components/ui/GameSelector.jsx';
import { useFirebase } from "./services/firebase.js";
import { calculateCompletion } from './utils/completion.js';
import AuthenticatedInviteHandler from './components/AuthenticatedInviteHandler.jsx';
import UnauthenticatedInviteHandler from './components/UnauthenticatedInviteHandler';
import NavigationHeader from './components/ui/NavigationHeader';
import WelcomeScreen from './components/ui/WelcomeScreen.jsx';
import AdminPanel from './components/admin/AdminPanel';
import ResultsAdmin from './components/admin/ResultsAdmin';
import { getFootballSeasonYear, getSeasonYearForLeague } from '../utils/season';
import { logger, replaceConsoleLogs } from "./utils/logger";

import { UNIFIED_CREDIT_PACKAGES, FAN_TYPE_MAPPINGS } from './utils/unified-credit-config';
import stripeService from './services/stripe-service';
import creditService from './services/credit-service';
import freeCodeService from './services/free-code-service';
import { ensureUserProfile } from './services/profile-service';
import FreeCodeHandler from './components/FreeCodeHandler';

if (process.env.NODE_ENV === 'production') {
  replaceConsoleLogs();
}

// Lazy pages
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const MyAnalyses = React.lazy(() => import('./components/dashboard/MyAnalyses'));

// minimal spinner
const LazyLoadingSpinner = () => (
  <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:200,fontSize:'1rem',color:'#6b7280',fontFamily:'Inter, sans-serif'}}>
    <div style={{animation:'spin 1s linear infinite',width:24,height:24,border:'2px solid #f3f4f6',borderTop:'2px solid #3b82f6',borderRadius:'50%',marginRight:12}} />
    Loading component...
    <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
  </div>
);

// expose (optional)
if (typeof window !== 'undefined') {
  window.UNIFIED_CREDIT_PACKAGES = UNIFIED_CREDIT_PACKAGES;
  window.FAN_TYPE_MAPPINGS = FAN_TYPE_MAPPINGS;
  window.stripeService = stripeService;
  window.creditService = creditService;
  window.freeCodeService = freeCodeService;
  // Simple helper to broadcast updated credits across UI
  window.updateUserCredits = (credits) => {
    try {
      window.dispatchEvent(new CustomEvent('creditsUpdated', { detail: credits }));
    } catch {}
  };
}

// status chip
const SaveStatusIndicator = ({ saveStatus, lastSaveTime, connectionStatus }) => {
  const show = saveStatus === 'saving' || saveStatus === 'error' || connectionStatus !== 'healthy';
  if (!show) return null;

  const color =
    saveStatus === 'saving' ? '#f59e0b' :
    saveStatus === 'error'  ? '#dc2626' :
    connectionStatus !== 'healthy' ? '#6b7280' : '#10b981';

  const text =
    saveStatus === 'saving' ? '💾 Saving...' :
    saveStatus === 'error'  ? '❌ Save Failed' :
    connectionStatus !== 'healthy' ? '⚠️ Offline' :
    lastSaveTime ? `✅ Saved ${new Date(lastSaveTime).toLocaleTimeString()}` : '✅ Saved';

  return (
    <div style={{
      position:'fixed',bottom:20,right:20,zIndex:1000,display:'flex',alignItems:'center',gap:8,
      padding:'8px 12px',background:'white',borderRadius:20,boxShadow:'0 4px 12px rgba(0,0,0,.1)',
      border:'1px solid #e5e7eb',fontSize:12,fontWeight:500,color
    }}>
      <div style={{width:8,height:8,borderRadius:'50%',background:color,animation: saveStatus==='saving' ? 'pulse 1s infinite': 'none'}} />
      <span>{text}</span>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
    </div>
  );
};

const AppContent = () => {
  // state
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState("welcome");
  const [navigationData, setNavigationData] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [connectionStatus] = useState('healthy');
  const [error, setError] = useState(null);

  // refs
  const inviteProcessedRef = useRef(false);
  const autoSaveTimeoutRef = useRef(null);
  const isInitializing = useRef(true);
  const lastStepRef = useRef(currentStep);
  const typingLockRef = useRef(false); // 👈 prevent autosave while typing

  // game data
  const [gameDataInternal, setGameDataInternal] = useState(() => ({
    groupMode: null,
    league: "",
    conference: "",
    division: "",
    team: "",
    teamId: "",
    participantName: "",
    avatar: "",
    fanType: "",
    season: getFootballSeasonYear(),
    activeTab: "breakdown",
    expandedPosition: null,
    expandedCoach: null,
    positionGrades: {},
    coachGrades: {},
    predictions: {
   topPlayer: { offensive: '', defensive: '', specialTeams: '' },
   seasonOutlook: '',
   gameResults: {},
   conferenceCoachRankings: {}
 },
    status: 'draft',
    groupCreated: false,
    inviteCode: "",
    groupId: null,
    groupName: "",
    isGroupCreator: false,
    completionPercentage: 0,
    lastUpdated: null,
    _existingDocId: null,
    _continuingExisting: false,
    teamSelected: false,
    _readOnly: false,
    _blockAutoSave: false
  }));
  const gameData = gameDataInternal;

  // services
  const sportsDataService = useMemo(() => new SportsDataService(), []);
  const {
    saveGameData,
    loadGameData,
    getUserAnalyses,
    createGroup,
    joinGroup,
    getGroupResults,
    submitAnalysis,
    updateGroup
  } = useFirebase() || {};

  // helper: setGameData
  const setGameData = useCallback((updater) => {
    setGameDataInternal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!next || typeof next !== 'object') return prev;
      // Merge the new state with the previous state
      return { ...prev, ...next };
    });
  }, []);

  // helper: createFreshGameState
  const createFreshGameState = useCallback((baseOptions = {}) => ({
    currentUser: gameData?.currentUser || baseOptions.currentUser || null,
    userId: gameData?.userId || baseOptions.userId || user?.uid || null,
    season: baseOptions.season || getFootballSeasonYear(),
    groupMode: baseOptions.groupMode || "solo",
    league: "", conference: "", division: "", team: "", teamId: "", teamSelected: false,
    positionGrades: {}, coachGrades: {}, predictions: { gameResults:{}, topPlayer:{}, conferenceCoachRankings:{} },
    status:'draft', completionPercentage:0, isSubmitted:false, isCompleted:false, submittedAt:null,
    participantName:"", avatar:"", fanType:null, formActivated:false,
    groupId:null, groupName:"", groupDescription:"", inviteCode:"", isGroupCreator:false, groupCreated:false, groupMembers:[],
    isJoiningPremiumGroup:false,
    _alreadyPaid:false, _skipPayment:false, _existingDocId:null, _continuingExisting:false, _updateExisting:false, continuingProgress:false,
    activeTab:"breakdown", expandedPosition:null, expandedCoach:null,
    createdAt:new Date().toISOString(), lastUpdated:new Date().toISOString(), lastReset:new Date().toISOString(),
    _readOnly:false, _blockAutoSave:false
  }), [gameData?.currentUser, gameData?.userId, user?.uid]);

  // cleanup payment abandon
  const cleanupPaymentAbandonmentData = useCallback(() => {
    localStorage.removeItem('sbe-temp-game-data');
    localStorage.removeItem('sbe-temp-fan-type');
    localStorage.removeItem('sbe-temp-current-step');
    localStorage.removeItem('sbe-payment-timestamp');
    localStorage.removeItem('sbe-navigation-context');
    sessionStorage.removeItem('stripe-payment-active');
    sessionStorage.removeItem('stripe-payment-start');
    sessionStorage.removeItem('payment_success_detected');
    setGameData(prev => ({
      ...prev,
      _paymentFlowActive: false,
      _redirectingToStripe: false,
      _paymentCancelled: false,
      _allowPaymentRetry: false,
      _lastPaymentAttempt: null,
      fanType: prev.fanType && ['dedicated','expert','diehard'].includes(prev.fanType) ? 'casual' : prev.fanType
    }));
  }, [setGameData]);

  // navigation
  const handleDashboardNavigation = useCallback((destination, selectedGameData = null) => {
    if (destination === 'dashboard' || !destination) {
      cleanupPaymentAbandonmentData();
    }

    if (destination === 'team') {
      const fresh = createFreshGameState({ groupMode: "solo" });
      setGameData(fresh);
      setCurrentStep('team');
    } else if (destination === 'createGroup') {
      const fresh = createFreshGameState({ groupMode: "create" });
      setGameData(fresh);
      setCurrentStep('createGroup');
    } else if (destination === 'joinGroup') {
      if (selectedGameData) setNavigationData(selectedGameData);
      setCurrentStep('joinGroup');
    } else if (destination === 'myanalyses') {
      setCurrentStep('myanalyses');
    } else if (destination === 'continue' && selectedGameData) {
      const continuation = {
        ...selectedGameData,
        _continuingExisting: true,
        _existingDocId: selectedGameData.id,
        continuingProgress: true,
        _alreadyPaid: !!(selectedGameData.participantName && selectedGameData.team && ['dedicated','expert'].includes(selectedGameData.fanType)),
        _skipPayment:  !!(selectedGameData.participantName && selectedGameData.team && ['dedicated','expert'].includes(selectedGameData.fanType)),
        _readOnly: selectedGameData?.status === 'submitted' || selectedGameData?.viewOnly === true,
        _blockAutoSave: selectedGameData?.status === 'submitted' || selectedGameData?.viewOnly === true
      };
      setGameData(continuation);
      if (!continuation.team) setCurrentStep('team');
      else if (!continuation.participantName) setCurrentStep('profile');
      else setCurrentStep('game');
    } else if (destination === 'game' && selectedGameData) {
      handleDashboardNavigation('continue', selectedGameData);
    } else {
      console.warn('Unknown dashboard destination:', destination);
    }

    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  }, [setGameData, setCurrentStep, createFreshGameState, setNavigationData, cleanupPaymentAbandonmentData]);

  const handleNewGame = useCallback(() => {
    const fresh = createFreshGameState({ groupMode: "solo" });
    setGameData(fresh);
    setCurrentStep("team");
  }, [createFreshGameState, setGameData, setCurrentStep]);

  const handleNavigation = useCallback((step, data = null) => {
    if (step === 'joinGroup' && data) setNavigationData(data);
    else setNavigationData(null);
    setCurrentStep(step);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
  }, []);

  // AUTOSAVE (debounced) — with guards
  const performAutoSave = useCallback(async (dataToSave) => {
    // 🛑 Skip autosave while user is typing in a text field
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
      const t = (active.getAttribute('type') || 'text').toLowerCase();
      if (t === 'text' || t === 'search' || active.tagName === 'TEXTAREA') {
        typingLockRef.current = true;
        // let the focus settle; autosave will run after debounce
        return;
      }
    } else {
      typingLockRef.current = false;
    }

    // read-only/submitted sessions should never create new docs
    if (!dataToSave || dataToSave._readOnly || dataToSave.status === 'submitted') {
      return;
    }

    if (dataToSave._blockAutoSave || dataToSave._joinInProgress) return;

    if (dataToSave.groupMode === 'join' && dataToSave.groupId && !dataToSave.participantName) {
      const recentJoin = dataToSave.createdAt && (Date.now() - new Date(dataToSave.createdAt).getTime()) < 30000;
      if (recentJoin) return;
    }

    if (!saveGameData || !user || user.isAnonymous || isInitializing.current) return;

    const isPremium = dataToSave.fanType && ['dedicated','expert','diehard'].includes(dataToSave.fanType);
    const isNew = !dataToSave._existingDocId && !dataToSave._continuingExisting;
    if (isPremium && isNew) {
      const ok = !!(dataToSave.creditConsumed || dataToSave._creditConsumedSuccessfully || dataToSave.paymentJustCompleted || dataToSave._paymentValidated || dataToSave._alreadyPaid || dataToSave._skipPayment);
      const hasAbandon = !!(dataToSave._paymentCancelled || dataToSave._allowPaymentRetry || dataToSave._lastPaymentAttempt);
      if (!ok || hasAbandon) return;
    }

    const hasProgress =
      (dataToSave.team && dataToSave.team.trim()) ||
      (dataToSave.participantName && dataToSave.participantName.trim()) ||
      (dataToSave.positionGrades && Object.keys(dataToSave.positionGrades).length) ||
      (dataToSave.coachGrades && Object.keys(dataToSave.coachGrades).length) ||
      (dataToSave.predictions && Object.keys(dataToSave.predictions).length) ||
      (dataToSave.fanType && dataToSave.fanType !== '') ||
      (dataToSave.completionPercentage && dataToSave.completionPercentage > 0);

    if (!hasProgress) return;

    try {
      setSaveStatus('saving');
      const cleaned = {
        ...dataToSave,
        _existingDocId: undefined,
        _continuingExisting: undefined,
        _alreadyPaid: undefined,
        _skipPayment: undefined,
        _paymentCancelled: undefined,
        _allowPaymentRetry: undefined,
        _lastPaymentAttempt: undefined,
        _paymentFlowActive: undefined,
        _redirectingToStripe: undefined,
        _blockAutoSave: undefined,
        _joinInProgress: undefined,
        _operationId: undefined,
        _readOnly: undefined
      };
      const id = await saveGameData(cleaned);
      if (id) {
        setSaveStatus('saved');
        setLastSaveTime(new Date().toISOString());
      } else {
        setSaveStatus('idle');
      }
    } catch (e) {
      console.error('Auto-save error', e);
      setSaveStatus('error');
      if (setError && !e.message?.includes('payment') && !e.message?.includes('validation')) {
        setError('Failed to save progress automatically');
      }
    }
  }, [saveGameData, user, setError]);

  const forceSave = useCallback(async () => {
    try {
      setSaveStatus('saving');
      await performAutoSave(gameData);
    } catch {
      setSaveStatus('error');
    }
  }, [performAutoSave, gameData]);

  // Starter points on first sign-in
  useEffect(() => {
    const run = async () => {
      try {
        if (user && !user.isAnonymous) {
          await ensureUserProfile(user, 100);
        }
      } catch {}
    };
    run();
  }, [user]);

  // team data helpers
  const getTeamSchedule = useCallback(async (teamIdentifier, teamName, season) => {
    const actualTeamName = teamName || gameData?.team || teamIdentifier;
    const league = gameData?.league || 'College';
    const resolvedSeason = season ?? getSeasonYearForLeague(league);
    if (!actualTeamName) return [];
    if (!sportsDataService?.getTeamSchedule) return [];
    return (await sportsDataService.getTeamSchedule(actualTeamName, league, resolvedSeason)) || [];
  }, [gameData?.team, gameData?.league, sportsDataService]);

  const getTeamRoster = useCallback(async (teamIdentifier, season) => {
    const teamName = gameData?.team || teamIdentifier;
    const league = gameData?.league || 'College';
    const resolvedSeason = season ?? getSeasonYearForLeague(league);
    if (!teamName) return [];
    if (!sportsDataService?.getTeamRoster) return [];
    return (await sportsDataService.getTeamRoster(teamName, league, resolvedSeason)) || [];
  }, [gameData?.team, gameData?.league, sportsDataService]);

  const getRosterByPosition = useCallback(async (teamIdentifier, season) => {
    const roster = await getTeamRoster(teamIdentifier, season);
    return roster.reduce((acc, p) => { const pos = p?.position || 'Unknown'; (acc[pos] ||= []).push(p); return acc; }, {});
  }, [getTeamRoster]);

  // submit
  const handleSubmission = useCallback(async (finalData) => {
    if (!submitAnalysis) throw new Error('submitAnalysis not available');
    setSaveStatus('saving');
    const submissionData = {
      ...finalData,
      isSubmitting: true,
      status: 'submitted',
      submittedAt: new Date().toISOString(),
      completionPercentage: calculateCompletion ? calculateCompletion(finalData).completionPercentage : 100,
      lastUpdated: new Date().toISOString()
    };
    const res = await submitAnalysis(submissionData);
    setGameData(prev => ({ ...prev, status:'submitted', submittedAt: submissionData.submittedAt, isSubmitting:false, completionPercentage: submissionData.completionPercentage }));
    setSaveStatus('saved');
    setLastSaveTime(new Date().toISOString());
    return res;
  }, [submitAnalysis, setGameData]);

  // when picking a different game in GameSelector
  const handleGameChange = useCallback((selectedGame) => {
    try {
      const isSubmitted = selectedGame?.status === 'submitted';
      const isViewOnly = isSubmitted || selectedGame?.viewOnly === true;

      const gameWithTracking = {
        ...selectedGame,
        _existingDocId: selectedGame.id,
        _continuingExisting: true,
        _readOnly: isViewOnly,
        _blockAutoSave: isViewOnly,
      };

      setGameData(gameWithTracking);
      // Reset join restriction flag when switching entries
      sessionStorage.removeItem('sbe-join-restricted');
      setCurrentStep("game");
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
    } catch (error) {
      console.error('❌ Error changing game:', error);
    }
  }, [setGameData]);

  const forceNewGame = useCallback(() => {
    const fresh = createFreshGameState({ groupMode: "solo" });
    setGameData(fresh);
    setCurrentStep('team');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
  }, [createFreshGameState, setGameData]);

  // completion memo
  const completion = useMemo(() => {
    try { return calculateCompletion(gameData); }
    catch { return { completionPercentage: 0, positions:{}, coaches:{}, predictions:{} }; }
  }, [gameData]);

  // 🔄 clear read-only flags when leaving the game
  useEffect(() => {
    if (lastStepRef.current === 'game' && currentStep !== 'game') {
      setGameData(prev => ({
        ...prev,
        _readOnly: false,
        _blockAutoSave: false
      }));
    }
    lastStepRef.current = currentStep;
  }, [currentStep, setGameData]);

  // browser back handling
  useEffect(() => {
    const onPop = (event) => {
      logger.navigation('🔙 Browser back/forward detected');
      const url = new URLSearchParams(window.location.search);
      const isPaymentReturn = url.get('session_id') || url.get('payment_success') || url.get('payment_cancelled');
      const active = sessionStorage.getItem('stripe-payment-active') === 'true';
      const start = sessionStorage.getItem('stripe-payment-start');
      const recentStripe = start && (Date.now() - parseInt(start, 10)) < 300000;

      if (isPaymentReturn || (active && recentStripe)) {
        event.preventDefault();
        return false;
      }

      if (currentStep === 'profile') {
        const recentJoin = gameData.groupMode === 'join' && gameData.groupId && gameData.team && !gameData.participantName;
        const hasJoinOp = gameData._joinInProgress || gameData._operationId;
        if (recentJoin || hasJoinOp) {
          event.preventDefault();
          return false;
        }
        if (gameData.team) setCurrentStep('team');
        else if (gameData.groupMode === 'solo' || gameData.isGroupCreator) setCurrentStep('team');
        else if (gameData.groupMode) setCurrentStep('joinGroup');
        else setCurrentStep('dashboard');
        return false;
      }

      if (currentStep === 'game' || currentStep === 'team') {
        setCurrentStep('dashboard');
        return false;
      }

      setCurrentStep('dashboard');
      return false;
    };

    if (user && !user.isAnonymous) {
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    }
  }, [currentStep, gameData, user]);

  // auth + invite (single effect)
  useEffect(() => {
    // Highest priority: stay on join if coming from AuthenticatedInviteHandler
    if (user && !user.isAnonymous && navigationData && navigationData.source === 'AuthenticatedInviteHandler') {
      inviteProcessedRef.current = true;
      if (currentStep !== 'joinGroup') setCurrentStep('joinGroup');
      return;
    }

    // Stored invite from unauthenticated flow
    if (user && !user.isAnonymous && !inviteProcessedRef.current) {
      const pendingInvite = localStorage.getItem('sbe_pending_invite');
      if (pendingInvite) {
        try {
          const inviteData = JSON.parse(pendingInvite);
          if (inviteData.source === 'UnauthenticatedInviteHandler') {
            setCurrentStep('joinGroup');
            setNavigationData({
              inviteCode: inviteData.inviteCode,
              autoFilled: true,
              fromSmartLink: true,
              authUserDirectJoin: false,
              flowType: inviteData.flowType
            });
            localStorage.removeItem('sbe_pending_invite');
            localStorage.removeItem('sbe_signup_invite_active');
            inviteProcessedRef.current = true;
            return;
          }
        } catch {
          localStorage.removeItem('sbe_pending_invite');
          localStorage.removeItem('sbe_signup_invite_active');
        }
      }
      inviteProcessedRef.current = true;
    }

    // unauth → welcome
    if (!user || user.isAnonymous) {
      if (currentStep !== "welcome") setCurrentStep("welcome");
      return;
    }

    // authed at welcome → dashboard
    if (user && !user.isAnonymous && currentStep === "welcome") {
      setCurrentStep("dashboard");
      return;
    }

    // payment cancelled
    const urlParams = new URLSearchParams(window.location.search);
    const paymentCancelled = urlParams.get('payment_cancelled') === 'true';
    if (paymentCancelled) {
      sessionStorage.removeItem('stripe-payment-active');
      sessionStorage.removeItem('stripe-payment-start');
      const stored = localStorage.getItem('sbe-temp-game-data');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setGameData(prev => ({ ...parsed, _paymentFlowActive:false, _redirectingToStripe:false, _paymentCancelled:true }));
        } catch {}
      }
      setCurrentStep('profile');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }

    // payment success/return handling: verify session and refresh credits
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success') === 'true' || urlParams.get('payment_success') === 'true';
    if (sessionId || success) {
      (async () => {
        try {
          // Verify with backend if sessionId is present
          if (sessionId && window.stripeService?.verifyCreditPurchase) {
            await window.stripeService.verifyCreditPurchase(sessionId);
          }
        } catch (e) {
          logger.warn?.('verifyCreditPurchase failed (non-fatal)', e?.message || e);
        } finally {
          try {
            const refreshed = await window.creditService?.checkBalance();
            if (refreshed) {
              window.dispatchEvent(new CustomEvent('creditsUpdated', { detail: refreshed }));
            }
          } catch {}
        }

        // Clean URL (remove Stripe params)
        try {
          const clean = window.location.pathname;
          window.history.replaceState({}, document.title, clean);
        } catch {}
      })();
    }

    // default: still on welcome? go dashboard
    if (user && !user.isAnonymous && inviteProcessedRef.current && !navigationData) {
      if (currentStep === "welcome") setCurrentStep("dashboard");
    }
  }, [user, currentStep, navigationData, setCurrentStep, setGameData]);

  // prevent double-consumption marker
  useEffect(() => {
    if (gameData?._autoConsumed && gameData?.creditConsumed && gameData?.fanType) {
      setGameData(prev => ({ ...prev, _alreadyPaid: true, _skipPayment: true, activeTab: 'breakdown' }));
    }
  }, [gameData?._autoConsumed, gameData?.creditConsumed, setGameData]);

  // debounced autosave (and don’t fight with typing)
  useEffect(() => {
    if (isInitializing.current) { isInitializing.current = false; return; }
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);

    autoSaveTimeoutRef.current = setTimeout(() => {
      // If user is still actively editing an input, push autosave a bit
      if (typingLockRef.current) {
        typingLockRef.current = false; // release and wait for next cycle
        return;
      }
      performAutoSave(gameData);
    }, 2000);

    return () => { if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current); };
  }, [gameData, performAutoSave]);

  // profile refresh restore
  useEffect(() => {
    if (currentStep === 'profile') {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      const raw = localStorage.getItem('sbe-temp-game-data');
      if (raw) {
        try {
          const data = JSON.parse(raw);
          if (data.team && !data._existingDocId) setGameData(data);
        } catch {}
      }
    }
  }, [currentStep, setGameData]);

  // shared props
  const sharedProps = useMemo(() => ({
    gameData,
    setGameData,
    setCurrentStep,
    sportsDataService,
    completion,
    getTeamSchedule,
    getTeamRoster,
    getRosterByPosition,
    saveGameData: saveGameData || (() => Promise.resolve()),
    loadGameData: loadGameData || (() => Promise.resolve()),
    getUserAnalyses: getUserAnalyses || (() => Promise.resolve([])),
    submitAnalysis: handleSubmission,
    getGroupResults: getGroupResults || (() => Promise.resolve()),
    saveStatus,
    lastSaveTime,
    connectionStatus,
    forceSave
  }), [
    gameData, setGameData, sportsDataService, completion, getTeamSchedule, getTeamRoster,
    getRosterByPosition, saveGameData, loadGameData, getUserAnalyses, handleSubmission,
    getGroupResults, saveStatus, lastSaveTime, connectionStatus, forceSave
  ]);

  const stepNavigationProps = useMemo(() => ({
    ...sharedProps,
    createGroup: createGroup || (() => Promise.resolve()),
    joinGroup: joinGroup || (() => Promise.resolve()),
    updateGroup: updateGroup || (() => Promise.resolve()),
    storeUserFlowBeforePayment: () => {}
  }), [sharedProps, createGroup, joinGroup, updateGroup]);

  // invite handlers cluster
  const inviteHandlers = (
    <>
      <FreeCodeHandler currentUser={user} />
      <AuthenticatedInviteHandler onNavigate={handleNavigation} currentUser={user} />
      <UnauthenticatedInviteHandler currentUser={user} />
    </>
  );

  // render
  const Screen = () => {
    if (currentStep === "welcome") {
      return (
        <ErrorBoundary>
          {inviteHandlers}
          <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <WelcomeScreen onNavigate={handleNavigation} />
          </div>
        </ErrorBoundary>
      );
    }

    if (currentStep === "dashboard") {
      return (
        <ErrorBoundary>
          {inviteHandlers}
          <FirebaseErrorBoundary showErrorDetails={process.env.NODE_ENV === 'development'}>
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px', fontFamily: 'Inter, sans-serif' }}>
              <Suspense fallback={<LazyLoadingSpinner />}>
                <Dashboard onNavigate={handleDashboardNavigation} gameData={gameData} setGameData={setGameData} />
              </Suspense>
            </div>
          </FirebaseErrorBoundary>
        </ErrorBoundary>
      );
    }

    if (currentStep === "myanalyses") {
      return (
        <ErrorBoundary>
          {inviteHandlers}
          <FirebaseErrorBoundary showErrorDetails={process.env.NODE_ENV === 'development'}>
            <div style={{ maxWidth: 1200, margin:'0 auto', padding:'0 16px', fontFamily:'Inter, sans-serif' }}>
              <NavigationHeader currentStep={currentStep} setCurrentStep={setCurrentStep} gameData={gameData} />
              <Suspense fallback={<LazyLoadingSpinner />}>
  <MyAnalyses
    onNavigate={(destination, data) => {
      // your component sometimes calls 'game' | 'team' | 'profile'
      // we want all of those to resume the selected existing analysis, not create a new one
      const needsContinue = destination === 'game' || destination === 'team' || destination === 'profile';
      if (needsContinue) {
        // ensure continue path (no dupes)
        handleDashboardNavigation('continue', data);
      } else {
        handleDashboardNavigation(destination, data);
      }
    }}
    onBack={() => setCurrentStep('dashboard')}
  />
</Suspense>
            </div>
          </FirebaseErrorBoundary>
        </ErrorBoundary>
      );
    }

    if (["group","createGroup","joinGroup","team","profile"].includes(currentStep)) {
      return (
        <ErrorBoundary>
          {inviteHandlers}
          <FirebaseErrorBoundary showErrorDetails={process.env.NODE_ENV === 'development'}>
            <div style={{ maxWidth: 1200, margin:'0 auto', padding:'0 16px', fontFamily:'Inter, sans-serif' }}>
              {currentStep === "group" && <ErrorBoundary><GroupSelection {...stepNavigationProps} /></ErrorBoundary>}
              {currentStep === "createGroup" && <ErrorBoundary><GroupCreationFlow {...stepNavigationProps} /></ErrorBoundary>}
              {currentStep === "joinGroup" && (
                <ErrorBoundary>
                  <JoinGroupFlow
                    {...stepNavigationProps}
                    inviteCode={navigationData?.inviteCode}
                    autoFilled={navigationData?.autoFilled}
                    authUserDirectJoin={navigationData?.authUserDirectJoin}
                    setNavigationData={setNavigationData}
                  />
                </ErrorBoundary>
              )}
              {currentStep === "team" && <ErrorBoundary><TeamSelection {...stepNavigationProps} /></ErrorBoundary>}
              {currentStep === "profile" && <ErrorBoundary><ProfileSetup {...stepNavigationProps} /></ErrorBoundary>}
            </div>
          </FirebaseErrorBoundary>
        </ErrorBoundary>
      );
    }

    if (currentStep === "game") {
      return (
        <ErrorBoundary>
          {inviteHandlers}
          <FirebaseErrorBoundary showErrorDetails={process.env.NODE_ENV === 'development'}>
            <GameProvider gameData={gameData} setGameData={setGameData}>
              <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 16px', fontFamily:'Inter, sans-serif' }}>
                <ErrorBoundary>
                  <GameSelector
                    currentGameData={gameData}
                    onGameChange={handleGameChange}
                    onNewGame={handleNewGame}
                    onNavigateToDashboard={() => setCurrentStep('dashboard')}
                    getUserAnalyses={getUserAnalyses}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <GameInterface {...sharedProps} />
                </ErrorBoundary>
              </div>
            </GameProvider>
          </FirebaseErrorBoundary>
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary>
        {inviteHandlers}
        <div style={{ padding:'2rem', fontFamily:'Inter, sans-serif', textAlign:'center' }}>
          <h2>Loading...</h2>
          <p>Initializing the application...</p>
          <div style={{ marginTop:'1rem', fontSize:'.9rem', color:'#6b7280' }}>Current step: {currentStep}</div>
        </div>
      </ErrorBoundary>
    );
  };

  return (
    <>
      <Screen />
      <SaveStatusIndicator
        saveStatus={saveStatus}
        lastSaveTime={lastSaveTime}
        connectionStatus={connectionStatus}
      />
    </>
  );
};

// Main App
const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryProvider>
          <FirebaseErrorBoundary showErrorDetails={process.env.NODE_ENV === 'development'}>
            <ErrorBoundary>
              <AppContent />
              <AuthModal />
              <AdminPanel />
              <ResultsAdmin />
            </ErrorBoundary>
          </FirebaseErrorBoundary>
        </QueryProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
